<template>
  <Portal
    v-if="confirmDialog.isRevealed.value"
    to="root-widget"
  >
    <ConfirmationModal
      :title="translate('helpFeedback')"
      :header-icon="'help-circle'"
      :confirm-button-text="translate('duplicate')"
      :show-confirm-buttons="false"
      :width-class="'w-[450px]'"
      @cancel="closeHelpPopup"
    >
      <template #content>
        <div class="project-window-help">
          <div class="popup-content">
            <div class="video-wrap usn">
              <iframe
                width="452"
                height="255"
                src="https://www.youtube.com/embed/fWQxk_5lfjI?rel=0&color=white"
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write;
                encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen="allowfullscreen"
              />
            </div>
          </div>
          <div class="popup-content-lower">
            <div class="list-help usn">
              <span
                v-for="section in onboardingSectionsToShow"
                :key="section.title"
                class="help-links"
                @click="section.onClick"
              >
                <div class="row-help">
                  <em
                    class="help-icon"
                    :style="getCustomIconStyle(iconPath(section.icon))"
                  />
                  <div class="text-area">
                    <strong>{{ section.title }}</strong>
                    <p class="subtext">{{ section.description }}</p>
                  </div>
                  <em
                    class="custom-icons arrow-right"
                    :style="getCustomIconStyle(iconPath('chevron-right'))"
                  />
                </div>
              </span>
              <a
                class="help-links"
                target="_blank"
                rel="noopener"
                href="https://join.slack.com/t/superthreadcommunity/shared_invite/zt-z0inhlyc-ifhwtGUNBM1G~z2NkMJIPg"
              >
                <div class="row-help">
                  <em
                    class="help-icon"
                    :style="getCustomIconStyle(iconPath('slack'))"
                  />
                  <div class="text-area">
                    <strong>{{ translate('joinSlack') }}</strong>
                    <p class="subtext">{{ translate('askQuestions') }}</p>
                  </div>
                  <em
                    class="custom-icons arrow-right"
                    :style="getCustomIconStyle(iconPath('chevron-right'))"
                  />
                </div>
              </a>
              <a
                target="_blank"
                rel="noopener"
                class="help-links"
                :href="$constants.changelogUrl"
              >
                <div class="row-help">
                  <em
                    class="help-icon"
                    :style="getCustomIconStyle(iconPath('rocket'))"
                  />
                  <div class="text-area">
                    <strong>{{ translate('whatsNew') }}</strong>
                    <p class="subtext">{{ translate('viewUpdates') }}</p>
                  </div>
                  <em
                    class="custom-icons arrow-right"
                    :style="getCustomIconStyle(iconPath('chevron-right'))"
                  />
                </div>
              </a>
              <div
                class="help-links"
                @click="openMailClient"
              >
                <div class="row-help">
                  <em
                    class="help-icon"
                    :style="getCustomIconStyle(iconPath('mail'))"
                  ></em>
                  <div class="text-area">
                    <strong>{{ translate('contactUs') }}</strong>
                    <div class="mail-section">
                      <p class="subtext">
                        {{ $constants.contactMail }}
                      </p>
                      <div
                        class="copy-icon-box"
                        @click="copyToClipboard($event, $constants.contactMail)"
                      >
                        <em
                          class="copy-mail-icon"
                          :style="getCustomIconStyle(iconPath('copy'))"
                        ></em>
                      </div>
                    </div>
                  </div>
                  <em
                    class="custom-icons arrow-right"
                    :style="getCustomIconStyle(iconPath('chevron-right'))"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ConfirmationModal>
  </Portal>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, computed } from 'vue';
import { Portal } from 'portal-vue';
import { useConfirmDialog } from '@vueuse/core';
import ConfirmationModal from '@/components/widgets/ConfirmationModal.vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import useNotes from '@/utilities/composables/useNotes';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';

const { iconPath, getCustomIconStyle } = useCustomIcon();
const { translate } = useTranslate();
const _this = getCurrentInstance()!.proxy as any;
const confirmDialog = useConfirmDialog();
const { createNoteFromOnboardingEvent } = useNotes();
const { isElectronApp } = useIsElectronApp();

onMounted(() => {
  confirmDialog.reveal();
  confirmDialog.onCancel((e: Event) => {
    closeHelpPopup(e);
  });
});

const onboardingSections = computed(() => {
  return [
    {
      title: translate('quickStart'),
      description: translate('getToKnow'),
      icon: 'zap',
      show: true,
      onClick: openOnboarding,
    },
    {
      title: translate('notesTutorial'),
      description: translate('learnHowToUseSuperthreadNotes'),
      icon: 'note',
      show: isElectronApp.value,
      onClick: openNotesTutorial,
    },
  ];
});

const onboardingSectionsToShow = computed(() => {
  return onboardingSections.value.filter((section) => section.show);
});

const openOnboarding = () => {
  _this.$store.dispatch('setShowOnboarding', true);
};

const openNotesTutorial = () => {
  closeHelpPopup();
  createNoteFromOnboardingEvent();
};

const closeHelpPopup = (event: Event) => {
  const { target } = event || {};

  if (target && (target as HTMLElement).matches('.ui-notification-text-wrap .close-icon')) {
    return;
  }
  _this.$store.dispatch('setHelpPopupIsOn', false);
};

const openMailClient = () => {
  const mailtoLink = `mailto:${_this.$constants.contactMail}`;
  window.location.href = mailtoLink;
};

const copyToClipboard = (event: Event, text: string) => {
  event.stopPropagation();
  navigator.clipboard.writeText(text);
  const notification = {
    message: translate('mailCopiedToClipboard'),
    icon: 'link-st',
    duration: 4000,
  };
  _this.$store.dispatch('addUiNotification', notification);
};
</script>

<style lang="scss">
.project-window-help {
  box-sizing: border-box;
  @apply bg-background-strongest;
  .video-wrap {
    height: 255px;
  }
  iframe {
    border-radius: 8px;
    width: 100%;
  }
  .popup-header {
    border-bottom: none;
    height: 37px;
  }
  .popup-content {
    display: flex;
    flex-direction: column;
    @apply border-dividers-default mb-6;
  }

  .popup-content-lower {
    display: flex;
    flex-direction: column;
    border-top: 1px solid;
    @apply border-dividers-default;

    .list-help {
      @apply flex flex-col mt-6 gap-2;
    }
  }

  .row-help {
    height: 66px;
    @apply flex items-center px-4 rounded-lg;
  }
  .row-help:hover {
    @apply bg-hover-default;
  }
  .help-icon {
    @apply bg-icon-default mr-6 w-6 h-6 shrink-0;
  }
  .arrow-right {
    @apply h-5 w-5 ml-auto;
  }
  .help-links {
    text-decoration: none;
    color: inherit;
    @apply cursor-pointer;
  }
  .text-area {
    @apply text-text-default;

    .mail-section {
      display: flex;
      align-items: center;

      .copy-icon-box {
        @apply flex items-center justify-center w-6 h-6 ml-1.5 rounded;
      }

      .copy-icon-box:hover {
        @apply bg-icon-bg;

        .copy-mail-icon {
          @apply bg-icon-default;
        }
      }

      .copy-mail-icon {
        @apply w-3.5 h-3.5 bg-text-placeholder;
      }
    }
  }
  .subtext {
    @apply text-text-placeholder;
  }
}
</style>
